import { createContext, type ReactNode, useContext, useSyncExternalStore } from 'react';

/** One tab's "How it works", shown inside the page-level ⓘ panel. */
export interface PageHelpSection {
  title: string;
  content: ReactNode;
}

/**
 * Tiny store behind the page-level ⓘ: the header's help panel says it is mounted, and each
 * tab's `PageIntro` hands it its "How it works" instead of drawing its own link — one help
 * entry point per page.
 */
export class PageHelpStore {
  private sections = new Map<string, PageHelpSection>();
  private panels = 0;
  private listeners = new Set<() => void>();
  private snapshot: PageHelpSection[] = [];

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit() {
    this.snapshot = [...this.sections.values()];
    this.listeners.forEach((l) => {
      l();
    });
  }

  getSections = () => this.snapshot;
  getPanelCount = () => this.panels;

  setSection(id: string, section: PageHelpSection) {
    this.sections.set(id, section);
    this.emit();
  }

  removeSection(id: string) {
    if (this.sections.delete(id)) this.emit();
  }

  addPanel() {
    this.panels += 1;
    this.emit();
    return () => {
      this.panels -= 1;
      this.emit();
    };
  }
}

export const PageHelpContext = createContext<PageHelpStore | null>(null);
export const PageHelpScopeContext = createContext(false);

/** The store, or null outside the catalog-v2 area. */
export function usePageHelpStore() {
  return useContext(PageHelpContext);
}

/** True when this ⓘ is the page-level one. */
export function useIsPageHelpScope() {
  return useContext(PageHelpScopeContext);
}

const NO_SECTIONS: PageHelpSection[] = [];
const noop = () => () => undefined;

/** Sections the tabs registered (empty outside the provider). */
export function usePageHelpSections(store: PageHelpStore | null) {
  return useSyncExternalStore(
    store ? store.subscribe : noop,
    store ? store.getSections : () => NO_SECTIONS,
    () => NO_SECTIONS,
  );
}

/** Whether a page-level ⓘ is on screen right now. */
export function usePageHelpMounted(store: PageHelpStore | null) {
  return (
    useSyncExternalStore(
      store ? store.subscribe : noop,
      store ? store.getPanelCount : () => 0,
      () => 0,
    ) > 0
  );
}
